import { Box, Button, Text, Textarea } from '@tocktix/pantry';
import { ChangeEvent, FunctionComponent, useRef, useState } from 'react';
import Papa from 'papaparse';

// Mirror of the server-side MAX_BULK_USER_IDS cap so the browser rejects oversized batches before
// spending a resolveUsers round-trip.
export const MAX_BULK_USER_IDS = 500;

// Split pasted free text into a de-duplicated, trimmed identifier list. Identifiers may be uuids,
// emails, phone numbers, or numeric ids, so we only split on commas and newlines (never spaces —
// some phone formats contain them) and keep each token verbatim.
export const parseIdentifiers = (raw: string): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of raw.split(/[\n,]+/)) {
    const trimmed = part.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
};

interface BulkUserInputProps {
  initialValue?: string;
  onContinue: (identifiers: string[]) => void;
}

const BulkUserInput: FunctionComponent<BulkUserInputProps> = ({
  initialValue = '',
  onContinue,
}) => {
  const [text, setText] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const identifiers = parseIdentifiers(text);

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setError(null);
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (results) => {
        const cells = results.data
          .flat()
          .map((cell) => (cell ?? '').trim())
          .filter(Boolean);
        if (cells.length === 0) {
          setError('No identifiers found in the CSV file.');
          return;
        }
        // Append the parsed values into the textarea so the operator can review/trim (e.g. a stray
        // header cell) before continuing to the resolve step.
        setText((prev) => (prev.trim() ? `${prev.trim()}\n${cells.join('\n')}` : cells.join('\n')));
      },
      error: () => setError('Could not read the CSV file.'),
    });
    // Reset so re-uploading the same file fires another change event.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContinue = () => {
    if (identifiers.length === 0) {
      setError('Enter at least one identifier.');
      return;
    }
    if (identifiers.length > MAX_BULK_USER_IDS) {
      setError(`Too many identifiers — max ${MAX_BULK_USER_IDS}, got ${identifiers.length}.`);
      return;
    }
    setError(null);
    onContinue(identifiers);
  };

  return (
    <Box display="flex" flexDirection="column" gap={300}>
      <Textarea
        id="bulk-user-identifiers"
        label="User identifiers"
        hint={`Paste comma- or newline-separated identifiers (uuid, email, phone, or numeric id). Max ${MAX_BULK_USER_IDS}.`}
        value={text}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
          setText(event.target.value);
          setError(null);
        }}
        rows={8}
        fullWidth
        validationStatus={error ? 'error' : undefined}
        data-testid="bulk-user-identifiers-input"
      />
      <Box display="flex" alignItems="center" gap={200}>
        <Text variant="caption" color="fgSubtle">
          Or import a CSV:
        </Text>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
          style={{ display: 'none' }}
          data-testid="bulk-user-csv-input"
          aria-label="Import identifiers from CSV"
        />
        <Button
          variant="outlined"
          label="Import CSV"
          onClick={() => fileInputRef.current?.click()}
          data-testid="bulk-user-csv-button"
        />
      </Box>
      {error && (
        <Text variant="body" color="fgDangerBase" data-testid="bulk-user-input-error">
          {error}
        </Text>
      )}
      <Box display="flex" alignItems="center" gap={200}>
        <Button
          label={identifiers.length ? `Continue (${identifiers.length})` : 'Continue'}
          onClick={handleContinue}
          disabled={identifiers.length === 0}
          data-testid="bulk-user-continue-button"
        />
      </Box>
    </Box>
  );
};

export default BulkUserInput;
